// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useCollateralAgreementChangeRequests from '../../../hooks/queries/collateral/collateral/useCollateralAgreementChangeRequests';
import useCollateralAgreementRequests from '../../../hooks/queries/collateral/collateral/useCollateralAgreementRequests';
import useCollateralAgreements from '../../../hooks/queries/collateral/collateral/useCollateralAgreements';
import { useGetCollateralState } from '../../../utils/useGetCollateralState';
import { CollateralAgreementRow } from '../collaterals/types';

export const useGetAgreementRows = () => {
  const collateralAgreementRequests = useCollateralAgreementRequests();
  const collateralAgreementChangeRequests = useCollateralAgreementChangeRequests();
  const collateralAgreements = useCollateralAgreements();
  const getCollateralState = useGetCollateralState();

  const isLoading =
    collateralAgreementRequests.isLoading ||
    !collateralAgreementRequests.data ||
    collateralAgreements.isLoading ||
    !collateralAgreements.data;

  const requestRows: CollateralAgreementRow[] = (collateralAgreementRequests?.data || []).map(
    (r) => ({
      contractId: r.contractId,
      payload: {
        partyA: r.payload.requestor, // requestorIsPartyA: true
        partyB: r.payload.counterparty,
        operator: r.payload.operator,
        requestor: r.payload.requestor,
        counterparty: r.payload.counterparty,
        id: r.payload.id,
        terms: r.payload.terms,
      },
      state: 'Pending',
    }),
  );

  const acceptedRows: CollateralAgreementRow[] = (collateralAgreements.data || []).map((r) => {
    const state = getCollateralState({
      partyA: r.payload.partyA,
      partyB: r.payload.partyB,
      operator: r.payload.operator,
      id: r.payload.id,
    });
    return {
      contractId: r.contractId,
      payload: {
        partyA: r.payload.partyA,
        partyB: r.payload.partyB,
        operator: r.payload.operator,
        counterparty: r.payload.partyB,
        requestor: r.payload.partyA,
        id: r.payload.id,
        terms: r.payload.terms,
        collateralPositions: state?.payload.collateralPositions,
      },
      state: 'Accepted',
    };
  });

  const changeRequestRows: CollateralAgreementRow[] = (
    collateralAgreementChangeRequests?.data || []
  ).map((r) => {
    const acceptedAgreement = acceptedRows.find(
      (ar) => ar.contractId === r.payload.collateralAgreementCid,
    );
    return {
      contractId: r.contractId,
      payload: {
        partyA: acceptedAgreement?.payload.partyA || '::',
        partyB: acceptedAgreement?.payload.partyB || '::',
        operator: r.payload.operator,
        requestor: r.payload.requestor,
        counterparty: r.payload.counterparty,
        id: r.payload.id,
        terms: r.payload.terms,
      },
      state: 'Change Requested',
    };
  });

  return {
    rows: [...requestRows, ...changeRequestRows, ...acceptedRows],
    isLoading,
  };
};
